"use client";

import { motion } from "framer-motion";

interface FAQItem {
  question: string;
  answer: string;
}

interface HomepageFAQsProps {
  title?: string;
  description?: string;
  faqs: FAQItem[];
  className?: string;
}

export function HomepageFAQs({ 
  title = "Frequently Asked Questions",
  description,
  faqs,
  className = ""
}: HomepageFAQsProps) {
  return (
    <div className={`w-full ${className}`}>
      {/* Left-aligned header with accent bar */}
      <div className="mb-10 lg:mb-12 px-4 lg:px-6 relative">
        {/* Accent bar */}
        <div 
          className="absolute left-0 top-0 w-0.5 h-10 rounded-full hidden lg:block"
          style={{ backgroundColor: 'var(--mininav-active-color)' }}
        />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-gray-900 mb-2">
            {title}
          </h2>
          {description && (
            <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed">
              {description}
            </p>
          )}
        </motion.div>
      </div>

      {/* Clean FAQ Grid - all items visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-12 gap-y-8 lg:gap-y-10 px-4 lg:px-6">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            viewport={{ once: true }}
            className="space-y-3"
          >
            <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug font-satoshi">
              {faq.question}
            </h3>
            <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed">
              {faq.answer}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
